// umlaut-check: skip — technische Schlüssel der JSON-Schnittstelle (laeuft, haengt, ueber) sind ASCII-Bezeichner.
import {useCallback, useEffect, useRef, useState} from 'react';
import {loadAutoReserve, startAutoReserve} from './api';

// Stand des automatischen Reservierens im Hintergrund (1.2.15). Der Server
// legt beim Speichern einen Lauf auf die Queue; die Oberfläche fragt den Stand
// nur ab — sie lädt die Seite nie neu. Solange ein Lauf läuft, wird alle drei
// Sekunden gefragt, danach nicht mehr.

const TAKT_MS = 3000;
/** So lange wird nach dem Speichern auf den Beginn eines Laufs gewartet, bevor Ruhe einkehrt. */
const ANLAUF_MS = 15000;

/**
 * @param {string} orderRef - Beleg-Referenz (docRef).
 * @param {boolean} enabled - nur im Auftragsmodus, nach dem Laden des Belegs.
 * @param {() => void} [onFertig] - wird nach jedem Lauf gerufen (z. B. Bestand neu holen).
 */
export default function useAutoReserve(orderRef, enabled, onFertig) {
    const [stand, setStand] = useState(null);
    const [fehler, setFehler] = useState(null);
    const [anlauf, setAnlauf] = useState(false);
    const [takt, setTakt] = useState(0);
    const timer = useRef(null);
    const anlaufTimer = useRef(null);
    const laeuftVorher = useRef(false);
    const fertigRef = useRef(onFertig);
    fertigRef.current = onFertig;

    const abfragen = useCallback(async () => {
        try {
            const daten = await loadAutoReserve(orderRef);
            setStand(daten);
            setFehler(null);
            return daten;
        } catch (e) {
            setFehler(e.response?.data?.error || e.message);
            return null;
        } finally {
            // Jede Antwort (auch ein Fehler) setzt den Takt für die nächste Frage.
            setTakt((n) => n + 1);
        }
    }, [orderRef]);

    // Erste Abfrage beim Öffnen: ein früherer Lauf bleibt sichtbar.
    useEffect(() => {
        if (!enabled) {
            return undefined;
        }
        abfragen();
        return undefined;
    }, [enabled, abfragen]);

    const laeuft = !!(stand && stand.laeuft);

    // Abfragen im Takt, solange ein Lauf läuft oder gleich beginnt.
    useEffect(() => {
        if (!enabled || (!laeuft && !anlauf)) {
            return undefined;
        }
        timer.current = setTimeout(abfragen, TAKT_MS);
        return () => clearTimeout(timer.current);
    }, [enabled, laeuft, anlauf, takt, abfragen]);

    // Lauf beendet: Bestand und Anzeige nachziehen.
    useEffect(() => {
        if (laeuftVorher.current && !laeuft) {
            if (fertigRef.current) {
                fertigRef.current();
            }
        }
        laeuftVorher.current = laeuft;
        if (laeuft) {
            setAnlauf(false);
        }
    }, [laeuft]);

    useEffect(() => () => {
        clearTimeout(timer.current);
        clearTimeout(anlaufTimer.current);
    }, []);

    /** Nach dem Speichern: der Server hat einen Lauf angelegt — auf ihn warten. */
    const nachSpeichern = useCallback(() => {
        if (!enabled) {
            return;
        }
        setAnlauf(true);
        clearTimeout(anlaufTimer.current);
        anlaufTimer.current = setTimeout(() => setAnlauf(false), ANLAUF_MS);
        abfragen();
    }, [enabled, abfragen]);

    /** Lauf von Hand anstoßen. Liefert eine Fehlermeldung oder null. */
    const anstossen = useCallback(async () => {
        try {
            await startAutoReserve(orderRef);
            nachSpeichern();
            return null;
        } catch (e) {
            return e.response?.data?.error || e.message;
        }
    }, [orderRef, nachSpeichern]);

    // „läuft" zeigt nur, was der Server bestätigt: Nach dem Speichern wird zwar
    // kurz öfter gefragt (der Lauf entsteht erst auf dem Server), aber ohne
    // Lauf erscheint nichts.
    return {stand, fehler, laeuft, anstossen, nachSpeichern, abfragen};
}
